import { useEffect, useState } from "react";
import api from "@/lib/api";
import { timeAgo } from "@/lib/utils-ros";
import { useAuth } from "@/lib/auth";
import { CheckCircle2, XCircle, Clock, Loader2, Filter, Check, ShieldCheck } from "lucide-react";
import { PageToolbar } from "@/components/revenueos/PageToolbar";
import { toast } from "sonner";

const STATUS_META = {
  pending: { color: "#C76500", background: "#FFF3E2", borderColor: "#F0C88E" },
  approved: { color: "#047857", background: "#E7F8F1", borderColor: "#A9DFC9" },
  rejected: { color: "#B42318", background: "#FFF1F0", borderColor: "#F0B9B5" },
  withdrawn: { color: "#607D8B", background: "#EDF2F4", borderColor: "#D2DEE3" },
};

export default function Approvals() {
  const { role } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);

  const canDecide = role === "super_admin" || role === "admin" || role === "founder";

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/approvals");
      setItems(data);
    } catch {
      toast.error("Could not load approvals");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function decide(id, decision) {
    setBusy(id);
    try {
      await api.post(`/approvals/${id}/decide`, { decision, notes: "" });
      toast.success(`Marked ${decision}`);
      await load();
    } catch { toast.error("Failed"); }
    finally { setBusy(null); }
  }

  const filtered = items.filter(a => {
    if (filter !== "all" && a.status !== filter) return false;
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return [a.entity_type, a.entity_id, a.reason, a.request_kind].filter(Boolean).join(" ").toLowerCase().includes(q);
  });
  const pendingCount = items.filter(a => a.status === "pending").length;

  return (
    <div className="min-h-full bg-[#F5FAFC] pb-12" data-testid="approvals-page">
      <section className="bg-[#07539A] px-5 md:px-9 pt-7 pb-16 shadow-[inset_0_-1px_0_rgba(255,255,255,0.12)]">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-5">
          <div>
            <h1 className="font-display text-3xl md:text-4xl tracking-tight !text-white">Approvals</h1>
            <p className="text-[13px] text-white/75 mt-1">Executive gates for discounts, commercial closes, and custom terms.</p>
          </div>
          <div className="text-left md:text-right">
            <div className="text-[10px] uppercase tracking-widest text-white/60">Pending decisions</div>
            <div className="font-display text-2xl tabular-nums !text-white">{pendingCount}</div>
          </div>
        </div>
      </section>

      <div className="px-4 md:px-8 -mt-7 relative z-10 space-y-5">
      <PageToolbar
        query={query}
        onQueryChange={setQuery}
        placeholder="Search by entity or reason…"
        entityLabel="approvals"
        count={filtered.length}
        variant="universe"
        right={(
          <div className="relative">
            <button
              type="button"
              onClick={() => setFilterOpen(open => !open)}
              data-testid="approvals-filter-btn"
              className={`h-9 px-3.5 rounded-full border text-[12px] font-semibold flex items-center gap-2 shadow-[0_5px_14px_rgba(7,83,154,0.08)] ${filter !== "all" ? "border-[#07539A] bg-[#E7F5FB] text-[#07539A]" : "border-[#BFD6E1] bg-white text-[#07539A] hover:bg-[#F3FAFD]"}`}
            >
              <Filter size={13} /> Filter
              {filter !== "all" && <span className="w-5 h-5 rounded-full bg-[#07539A] !text-white text-[10px] flex items-center justify-center">1</span>}
            </button>
            {filterOpen && (
              <div className="absolute right-0 top-11 z-30 w-52 rounded-2xl border border-[#C9DCE6] bg-white p-2 shadow-[0_18px_44px_rgba(7,83,154,0.18)]" data-testid="approvals-filter-menu">
                <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-widest text-[#07539A]">Approval status</div>
                {[{ key: "all", label: "All approvals" }, { key: "pending", label: "Pending" }, { key: "approved", label: "Approved" }, { key: "rejected", label: "Rejected" }].map(option => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => { setFilter(option.key); setFilterOpen(false); }}
                    data-testid={`appr-filter-${option.key}`}
                    className={`w-full px-3 py-2.5 rounded-xl flex items-center text-left text-[12px] ${filter === option.key ? "bg-[#E7F5FB] text-[#07539A] font-semibold" : "text-[#243B53] hover:bg-[#F3FAFD]"}`}
                  >
                    <span className="flex-1">{option.label}</span>
                    {filter === option.key && <Check size={13} />}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      />

      {loading ? <div className="bg-white border border-[#C9DCE6] rounded-[22px] text-[#78909C] p-10 text-center shadow-[0_8px_22px_rgba(7,83,154,0.06)]">Loading approvals…</div> :
       filtered.length === 0 ? (
         <div className="text-center p-10 border border-dashed border-[#B8D3DF] rounded-[22px] bg-white text-[13px] text-[#607D8B] shadow-[0_8px_22px_rgba(7,83,154,0.04)]">
           <span className="w-11 h-11 rounded-xl bg-[#E7F5FB] text-[#07539A] flex items-center justify-center mx-auto mb-3"><ShieldCheck size={20} /></span>
           No {filter === "all" ? "" : filter + " "}approvals.
         </div>
       ) : (
        <div className="grid gap-3">
          {filtered.map(a => (
            <div key={a.id} data-testid={`approval-${a.id}`} className="p-4 md:p-5 flex items-start gap-3 border border-[#C9DCE6] rounded-[22px] bg-white shadow-[0_8px_22px_rgba(7,83,154,0.06)]">
              <span className="w-9 h-9 rounded-xl bg-[#E7F5FB] text-[#07539A] flex items-center justify-center shrink-0"><Clock size={15} /></span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[9px] uppercase tracking-widest px-2.5 py-1 rounded-full font-mono border"
                        style={STATUS_META[a.status] || STATUS_META.withdrawn}>
                    {a.status}
                  </span>
                  <span className="text-[10px] uppercase tracking-wider text-[#607D8B]">{a.entity_type} · {a.request_kind}</span>
                  <span className="text-[10px] text-[#78909C]">{timeAgo(a.created_at)}</span>
                </div>
                <div className="text-[13px] font-medium text-[#243B53] mt-1.5">{a.reason}</div>
                {a.delta && (
                  <div className="text-[11px] text-[#607D8B] mt-1 font-mono bg-[#F7FBFD] border border-[#DCEAF0] rounded-xl px-3 py-2">
                    Delta: {JSON.stringify(a.delta)}
                  </div>
                )}
                {a.decision_notes && (
                  <div className="text-[11px] text-[#607D8B] mt-1">Notes: {a.decision_notes}</div>
                )}
              </div>
              {canDecide && a.status === "pending" && (
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => decide(a.id, "approved")} disabled={busy === a.id} data-testid={`appr-approve-${a.id}`}
                          className="h-9 px-3.5 rounded-xl bg-[#047857] hover:bg-[#036B4D] !text-white text-[11px] font-semibold flex items-center gap-1.5 disabled:opacity-50 shadow-[0_5px_14px_rgba(4,120,87,0.16)]">
                    {busy === a.id ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />} Approve
                  </button>
                  <button onClick={() => decide(a.id, "rejected")} disabled={busy === a.id} data-testid={`appr-reject-${a.id}`}
                          className="h-9 px-3.5 rounded-xl border border-[#E49B96] bg-white text-[#B42318] hover:bg-[#FFF1F0] text-[11px] font-semibold flex items-center gap-1.5 disabled:opacity-50">
                    <XCircle size={11} /> Reject
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      </div>
    </div>
  );
}
